import type { TFunction } from 'react-i18next';
import { addError, addInfo, deleteInfoByText } from '@/app/actions/snackbar/snackbar';
import { getScenarios } from '@/app/actions/scenarios/scenarioAction';
import { getInvitations } from '@/app/actions/userManagement/userManagementActions';
import type { AppDispatch } from '@/app/store';
import { getTypedPayload } from '@/types/realtime';
import type { KnownTaskType, UserNotificationDto } from '@/types/realtime';

type NotificationHandlerFn<T extends KnownTaskType = KnownTaskType> = (
  msg: UserNotificationDto & { taskType: T },
  dispatch: AppDispatch,
  t: TFunction
) => void;

export const getServerMessage = (payload: unknown, fallback: string): string => {
  return payload &&
    typeof payload === 'object' &&
    'Message' in payload &&
    typeof (payload as { Message: unknown }).Message === 'string'
    ? (payload as { Message: string }).Message
    : fallback;
};

export function handleFailure(msg: UserNotificationDto, dispatch: AppDispatch): void {
  const errorText = `❌ ${getServerMessage(msg.payload, `${msg.taskType} failed`)}`;
  dispatch(addError({ errorText }));

  if (msg.taskType === 'InviteUser') {
    dispatch(deleteInfoByText({ infoText: 'Sending invitation' }));
  }
}

function handleInviteUser(
  msg: UserNotificationDto & { taskType: 'InviteUser' },
  dispatch: AppDispatch,
  t: TFunction
): void {
  const payload = getTypedPayload(msg);
  dispatch(addInfo({ infoText: `✅ ${t('userInvitation', { email: payload.Email })}` }));

  if (msg.status === 'Completed') {
    dispatch(getInvitations());
  }
}

function handleCopyScenario(
  msg: UserNotificationDto & { taskType: 'CopyScenario' },
  dispatch: AppDispatch,
  t: TFunction
): void {
  const payload = getTypedPayload(msg);
  const scenarioId = String(payload.ScenarioId);
  const scenarioUrl = `${window.location.origin}/scene/${scenarioId}`;
  const infoText = t('scenarioCopyReady', { scenarioName: payload.ScenarioName });

  if (msg.status === 'Completed') {
    dispatch(getScenarios());
  }

  dispatch(addInfo({ infoText, link: scenarioUrl, linkText: payload.ScenarioName }));
}

export const notificationHandlers: Partial<{ [K in KnownTaskType]: NotificationHandlerFn<K> }> = {
  InviteUser: handleInviteUser,
  CopyScenario: handleCopyScenario
};

export function dispatchKnownNotificationHandler<T extends KnownTaskType>(
  msg: UserNotificationDto & { taskType: T },
  dispatch: AppDispatch,
  t: TFunction
): boolean {
  const handler = notificationHandlers[msg.taskType] as NotificationHandlerFn<T> | undefined;
  if (!handler) {
    return false;
  }

  handler(msg, dispatch, t);
  return true;
}
